import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import detailingIcon from '../assets/icons/detailing-icon.svg';
import oilChangeIcon from '../assets/icons/oil-change-icon.svg';
import tiresIcon from '../assets/icons/tires-icon.svg';
import CostBreakdownBar from '../components/CostBreakdownBar';
import LegalTooltip from '../components/LegalTooltip';
import PrizeTile from '../components/PrizeTile';
import RewardsBreakdown from '../components/RewardsBreakdown';
import VincentricCredit from '../components/VincentricCredit';

// Category data
const fixedCategoryData = [
  {
    name: 'Depreciation',
    pointMultiplier: 0,
    color: '#084045',
    description: 'Estimated decline in value over 5 years, based on Black Book residual values by year, make, model, and trim. Reflects projected resale value at the end of ownership.'
  },
  { 
    name: 'Financing', 
    pointMultiplier: 2, 
    color: '#5D5D5D',
    description: 'Assumes a 15% down payment, a 60-month loan term, and average market interest rates by state. Includes any available manufacturer (OEM) rebates.'
  },
  { 
    name: 'Insurance', 
    pointMultiplier: 2, 
    color: '#F7B801',
    description: 'Based on estimated average annual premiums for drivers with 6+ years of experience and a clean driving history. Rates vary by location, vehicle type, and insurer.'
  },
  { 
    name: 'Fuel',
    pointMultiplier: 2,
    color: '#FF6B35',
    description: 'Based on EPA-estimated fuel economy for your vehicle’s year, make, and model, and assumes a default driving mix of 45% highway and 55% city. Fuel prices vary by state and are updated monthly using national fuel price data.'
  },
  { 
    name: 'Maintenance', 
    pointMultiplier: 2, 
    color: '#8B5CF6',
    description: 'Based on the manufacturer’s recommended maintenance schedule. Includes oil changes, tire rotations, and other wear-item replacements like tires and batteries. EV/PHEV battery costs not included.'
  },
  { 
    name: 'Repairs', 
    pointMultiplier: 3,
    color: '#10B981',
    description: 'Based on the cost of a zero-deductible extended warranty. Covers unscheduled repairs needed to keep the vehicle in working condition after warranty coverage ends.',
    pointName: 'Up to 3x points',
    pointDescription: (
      <span>
        Earn 3x points per dollar when you shop with{' '}
        <a
          href="https://www.fasten.cc/rewards-partners"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Fasten Partners
        </a>
        . Eligible purchases must be made directly with a participating partner. See{' '}
        <a
          href="https://assets.fasten.cc/legal/terms-and-conditions.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Rewards Terms and Conditions
        </a>
        .
      </span>
    )
  },
  { 
    name: 'Taxes & Fees', 
    pointMultiplier: 0, 
    color: '#F59E0B',
    description: 'Includes estimated state and local sales tax, title and registration fees, and any additional one-time charges at purchase. Rates are based on your location and vehicle price.'
  }
];

// Prize tiles data
const prizeTiles = [
  {
    header: 'High-quality oil change and tire rotation',
    icon: oilChangeIcon,
    dollarAmount: 100
  },
  {
    header: 'Full detailing and brake pad replacement',
    icon: detailingIcon,
    dollarAmount: 500
  },
  {
    header: 'Brand new set of all-season tires',
    icon: tiresIcon,
    dollarAmount: 1000
  }
];

function DesktopLayout({ categories, navigate, isMonthlyView, setIsMonthlyView, vehicleInfo }) {
  DesktopLayout.propTypes = {
    categories: PropTypes.array.isRequired,
    navigate: PropTypes.func.isRequired,
    isMonthlyView: PropTypes.bool.isRequired,
    setIsMonthlyView: PropTypes.func.isRequired,
    vehicleInfo: PropTypes.object,
  }
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 font-sans py-24 px-20">
      {/* Header */}
      <div className="w-full max-w-7xl flex justify-between items-center mb-8">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm font-medium text-black hover:text-gray-600 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="pt-[3px]">
            {vehicleInfo ? `${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}` : 'Select Vehicle'}
          </span>
        </button>
        <LegalTooltip />
      </div>
      
      <div className="flex gap-6 w-full max-w-7xl">
        {/* Left Side - Cost Breakdown and Prize Tiles */}
        <div className="flex-1" style={{ flexBasis: '66.666667%' }}>
          {/* Cost Breakdown Box */}
          <div className="bg-white rounded-[12px] p-6 flex flex-col w-full border border-gray-200">
            <CostBreakdownBar categories={categories} isMonthlyView={isMonthlyView} setIsMonthlyView={setIsMonthlyView} />
          </div>
          
          {/* Prize Tiles Section */}
          <div className="mt-8 p-6">
            <h3 className="text-lg font-semibold text-black mb-6">What are Fasten Rewards worth?</h3>
            <div className="flex flex-wrap gap-4 justify-center">
              {prizeTiles.map((tile, index) => (
                <PrizeTile 
                  key={index}
                  header={tile.header}
                  icon={tile.icon}
                  dollarAmount={tile.dollarAmount}
                />
              ))}
            </div>
            <div className="flex justify-center mt-6">
              <VincentricCredit />
            </div>
          </div>
        </div>
        
        {/* Right Side - Rewards Breakdown Box */}
        <div className="flex-1 max-w-[320px]" style={{ flexBasis: '33.333333%' }}>
          <RewardsBreakdown categories={categories} />
        </div>
      </div>
    </div>
  );
}

function MobileLayout({ categories, navigate, isMonthlyView, setIsMonthlyView, vehicleInfo }) {
  MobileLayout.propTypes = {
    categories: PropTypes.array.isRequired,
    navigate: PropTypes.func.isRequired,
    isMonthlyView: PropTypes.bool.isRequired,
    setIsMonthlyView: PropTypes.func.isRequired,
    vehicleInfo: PropTypes.object,
  }
  return (
    <div className="min-h-screen flex flex-col bg-gray-100 font-sans pt-10 pb-5 px-4">
      {/* Header */}
      <div className="w-full flex justify-between items-center mb-8">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm font-medium text-black hover:text-gray-600 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="pt-[3px]">
            {vehicleInfo ? `${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}` : 'Select Vehicle'}
          </span>
        </button>
      </div>
      
      <div className="flex flex-col gap-6 w-full">
        {/* Cost Breakdown Box - First */}
        <div className="bg-white rounded-[12px] p-6 flex flex-col w-full border border-gray-200">
          <CostBreakdownBar categories={categories} isMonthlyView={isMonthlyView} setIsMonthlyView={setIsMonthlyView} />
        </div>
        
        {/* Rewards Breakdown Box - Second */}
        <RewardsBreakdown categories={categories} />
        
        {/* Prize Tiles Section - Third */}
        <div className="mt-8 p-6">
          <h3 className="text-lg font-semibold text-black mb-6">What are Fasten Rewards worth?</h3>
          <div className="flex flex-wrap gap-4 justify-center">
            {prizeTiles.map((tile, index) => (
              <PrizeTile 
                key={index}
                header={tile.header}
                icon={tile.icon}
                dollarAmount={tile.dollarAmount}
              />
            ))}
          </div>
          <div className="flex justify-center mt-6">
            <VincentricCredit />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMonthlyView, setIsMonthlyView] = useState(false);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Extract categories data and vehicle info from navigation state
  const categoriesData = location.state?.categoriesData;
  
  // Extract vehicle info from navigation state
  const vehicleInfo = location.state?.vehicleInfo || null;
  
  // Process categories based on monthly view
  const processCategoriesForView = (categories) => {
    return categories.map(category => ({
      ...category,
      dollarAmount: isMonthlyView ? category.dollarAmount / 60 : category.dollarAmount,
    }));
  };
  
  // Map over fixedCategoryData to maintain its order
  const categories = fixedCategoryData.map(fixedCategory => {
    let dollarAmount = categoriesData[fixedCategory.name] || 0;
    return {
      ...fixedCategory,
      dollarAmount,
    };
  }).filter(category => category.dollarAmount > 0); // Filter out categories with $0
  
  // Process categories for current view (monthly or total)
  const processedCategories = processCategoriesForView(categories);
  
  return (
    <>
      <div className="hidden lg:block">
        <DesktopLayout categories={processedCategories} navigate={navigate} isMonthlyView={isMonthlyView} setIsMonthlyView={setIsMonthlyView} vehicleInfo={vehicleInfo} />
      </div>
      <div className="block lg:hidden">
        <MobileLayout categories={processedCategories} navigate={navigate} isMonthlyView={isMonthlyView} setIsMonthlyView={setIsMonthlyView} vehicleInfo={vehicleInfo} />
      </div>
    </>
  );
} 